import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { Note } from '@/redux/internal-services/notesApi';
import {
  useAnalyzeNoteMutation,
  useConvertNoteItemMutation,
  useDeleteNoteMutation,
  useGetNoteConfigQuery,
  useGetNoteQuery,
  useRegenerateAnalysisMutation,
  useSaveNoteMutation,
  useUpdateNoteMutation,
} from '@/redux/internal-services/notesApi';
import { useGetMeetingsQuery, useGetPropertiesQuery } from '@/redux/internal-services/workspaceApi';
import type { CreateTaskInput } from '@/redux/internal-services/workspaceApiExtensions';
import { IconSparkle } from '@/components/notes/icons';
import { STATUS_BADGE } from '@/components/notes/noteUtils';
import { SuggestionCards } from '@/components/notes/SuggestionCards';

interface Props {
  note: Note;
  onSaved: (note: Note) => void;
  onDiscard: () => void;
  onDone: () => void;
  onOpenTask?: (taskId: string) => void;
  onNoteSynced?: (note: Note) => void;
  /** Caller sets this ref so the SAM EXECUTE_NOTE_FOLLOWUPS action can trigger batch-create. */
  onExecuteFollowupsRef?: MutableRefObject<(() => void) | null>;
  /** Caller sets this ref so the SAM SET_NOTE_FOLLOWUP_INCLUDED action can check/uncheck one,
   *  several, or all follow-up suggestions (the same checkbox the user can click). */
  onToggleFollowupsRef?: MutableRefObject<((opts: { suggestionId?: string; suggestionIds?: string[]; included: boolean }) => void) | null>;
  /** Caller sets this ref so the SAM EXPAND_NOTE_FOLLOWUP action can expand a follow-up's
   *  review/edit panel (optionally prefilling fields like due_date) without creating anything. */
  onExpandFollowupRef?: MutableRefObject<((opts: { suggestionId: string; fields?: Record<string, unknown> }) => void) | null>;
  /** Called after a delete/save/convert so the caller can refresh any cached notes-list card that
   *  might otherwise still show this note (deleted) or its pre-write state (saved/converted). */
  onNotesListChanged?: () => void;
}

export function NoteReviewCard({ note, onSaved, onDiscard, onDone, onOpenTask, onNoteSynced, onExecuteFollowupsRef, onToggleFollowupsRef, onExpandFollowupRef, onNotesListChanged }: Props) {
  const { t } = useTranslation();
  const { data: config } = useGetNoteConfigQuery();
  const { data: properties = [] } = useGetPropertiesQuery();
  const { data: meetings = [] } = useGetMeetingsQuery();
  const { data: liveNote = note } = useGetNoteQuery(note.note_id);

  const initialTranscript = note.clean_transcript || note.final_transcript || '';
  const [transcript, setTranscript] = useState(initialTranscript);
  const [analyzedText, setAnalyzedText] = useState(initialTranscript);
  const [tags, setTags] = useState<string[]>(
    note.custom_tags?.length ? note.custom_tags : (note.suggested_classifications || []),
  );
  const [linkType, setLinkType] = useState(
    note.context?.context_type === 'property' ? 'property'
    : note.context?.context_type === 'meeting' ? 'meeting' : '',
  );
  const [propertyId, setPropertyId] = useState(
    note.context?.context_type === 'property' ? (note.context?.entity_id || '') : '',
  );
  const [meetingId, setMeetingId] = useState(
    note.context?.context_type === 'meeting' ? (note.context?.entity_id || '') : '',
  );
  const [included, setIncluded] = useState<Record<string, boolean>>({});
  const [suggestionFields, setSuggestionFields] = useState<Record<string, CreateTaskInput>>({});
  const [creatingAll, setCreatingAll] = useState(false);
  // Per-suggestion-label error messages from the last "Create N" batch — Promise.allSettled means
  // one failing suggestion (e.g. a meeting with no date) must NOT hide that it failed just because
  // the others in the same batch succeeded.
  const [createErrors, setCreateErrors] = useState<string[]>([]);

  const lastSyncedOn = useRef(note.updated_on);
  useEffect(() => {
    if (liveNote.updated_on === lastSyncedOn.current) return;
    lastSyncedOn.current = liveNote.updated_on;
    const nextTranscript = liveNote.clean_transcript || liveNote.final_transcript || '';
    setTranscript(nextTranscript);
    setAnalyzedText(nextTranscript);
    setTags(liveNote.custom_tags?.length ? liveNote.custom_tags : (liveNote.suggested_classifications || []));
    if (liveNote.context?.context_type === 'property') {
      setLinkType('property'); setPropertyId(liveNote.context.entity_id || ''); setMeetingId('');
    } else if (liveNote.context?.context_type === 'meeting') {
      setLinkType('meeting'); setMeetingId(liveNote.context.entity_id || ''); setPropertyId('');
    } else {
      setLinkType(''); setPropertyId(''); setMeetingId('');
    }
    onNoteSynced?.(liveNote);
  }, [liveNote, onNoteSynced]);

  const onNoteSyncedRef = useRef(onNoteSynced);
  onNoteSyncedRef.current = onNoteSynced;
  const editReadyRef = useRef(false);
  useEffect(() => {
    if (!editReadyRef.current) { editReadyRef.current = true; return; }
    const timer = setTimeout(() => {
      const p = properties.find((pp) => pp.property_id === propertyId);
      const m = meetings.find((mm) => mm.meeting_id === meetingId);
      onNoteSyncedRef.current?.({
        ...liveNote,
        clean_transcript: transcript,
        custom_tags: tags,
        context: linkType === 'property' && propertyId
          ? { context_type: 'property', entity_id: propertyId, display_label: p?.address?.display_line ?? null }
          : linkType === 'meeting' && meetingId
          ? { context_type: 'meeting', entity_id: meetingId, display_label: m?.title ?? null }
          : null,
      } as Note);
    }, 500);
    return () => clearTimeout(timer);
  }, [transcript, tags, linkType, propertyId, meetingId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIncluded((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const s of liveNote.suggestions || []) {
        if (!(s.suggestion_id in next)) { next[s.suggestion_id] = true; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [liveNote.suggestions]);

  const [updateNote, { isLoading: updating }] = useUpdateNoteMutation();
  const [saveNote, { isLoading: finalizing }] = useSaveNoteMutation();
  const [analyzeNote, { isLoading: analyzing }] = useAnalyzeNoteMutation();
  const [deleteNote, { isLoading: deleting }] = useDeleteNoteMutation();
  const [regenerate, { isLoading: regenerating }] = useRegenerateAnalysisMutation();
  const [convertItem] = useConvertNoteItemMutation();
  const busy = updating || finalizing || analyzing || deleting;

  const saved = liveNote.status === 'saved';
  const allTags = config?.classification_tags || [];
  const dirty = !saved && transcript.trim() !== analyzedText.trim();
  const selectedProperty = properties.find((p) => p.property_id === propertyId);
  const hasContent = transcript.trim().length > 0 || !!liveNote.audio_url;
  const canSave = hasContent && !busy && (linkType !== 'property' || !!propertyId) && (linkType !== 'meeting' || !!meetingId);
  const summary = liveNote.review_summary || liveNote.summary;
  const analysed = !!(summary || liveNote.suggestions?.length || (liveNote.suggested_classifications?.length ?? 0));
  const followUpSummary = liveNote.follow_up_summary || liveNote.summary;
  const selectedMeeting = meetings.find((m) => m.meeting_id === meetingId);

  const noteType = useMemo(() => {
    const platform = ['site_observation', 'inspection', 'measurement', 'meeting_note', 'follow_up', 'general'];
    return tags.find((tg) => platform.includes(tg)) || note.note_type || 'general';
  }, [tags, note.note_type]);

  const toggleTag = (tag: string) => {
    if (saved) return;
    setTags((prev) => (prev.includes(tag) ? prev.filter((x) => x !== tag) : [...prev, tag]));
  };

  const contextBody = () => {
    if (linkType === 'property' && selectedProperty)
      return { context_type: 'property', entity_id: selectedProperty.property_id, display_label: selectedProperty.address?.display_line ?? null };
    if (linkType === 'meeting' && meetingId)
      return { context_type: 'meeting', entity_id: meetingId, display_label: selectedMeeting?.title ?? null };
    return undefined;
  };

  const runReanalyze = async () => {
    await updateNote({ id: note.note_id, body: { clean_transcript: transcript } }).unwrap().catch(() => undefined);
    const re = await analyzeNote(note.note_id).unwrap().catch(() => undefined);
    setAnalyzedText(transcript);
    if (re?.suggested_classifications?.length) setTags(re.suggested_classifications);
  };

  const handleDiscard = async () => {
    // Hard-delete the note so it disappears from the list entirely (not just status=discarded).
    await deleteNote(note.note_id).catch(() => undefined);
    // notesApi's own 'Note' cache is invalidated by the mutation, but the SAM-driven workspace
    // notes-list card (a separate cache — see refreshModuleCard in useChatWorkspace) isn't; without
    // this the deleted note keeps showing when the user navigates back to that list.
    onNotesListChanged?.();
    onDiscard();
  };

  // Every unconfirmed, undismissed, checked suggestion — drives the single action button's
  // label ("Save" vs "Save & create N" / "Create N" once already saved).
  const pendingSuggestionCount = (liveNote.suggestions || []).filter(
    (s) => !s.confirmed && !s.dismissed && (included[s.suggestion_id] ?? true),
  ).length;

  // Create every checked, unconfirmed suggestion in one batch (task/communication/meeting — any
  // target). Used standalone (post-save) and as part of handleSave (pre-save). Returns the list of
  // failure messages (empty = full success) — callers use the RETURN VALUE, not `createErrors`
  // state, to decide whether to close the card: state updates are async, so reading `createErrors`
  // right after calling this would still see the previous (stale) value.
  const createAllSelected = async (): Promise<string[]> => {
    const toCreate = (liveNote.suggestions || []).filter(
      (s) => !s.confirmed && !s.dismissed && (included[s.suggestion_id] ?? true),
    );
    if (!toCreate.length) return [];
    setCreatingAll(true);
    setCreateErrors([]);
    try {
      const results = await Promise.allSettled(
        toCreate.map((s) => {
          const target = s.target || 'task';
          const baseFields = (s.payload || {}) as Record<string, unknown>;
          const userFields = (suggestionFields[s.suggestion_id] || {}) as unknown as Record<string, unknown>;
          // Merge: user edits override AI payload; ensure task has a title fallback.
          const merged: Record<string, unknown> = { ...baseFields, ...userFields };
          if (target === 'task' && !merged.title) merged.title = s.label || 'Follow-up from note';
          return convertItem({
            id: liveNote.note_id,
            suggestionId: s.suggestion_id,
            target,
            fields: merged,
          }).unwrap();
        }),
      );
      // Promise.allSettled silently drops failures — one bad suggestion (e.g. a meeting with no
      // date) must not look identical to a successful batch just because siblings succeeded.
      const failures = results
        .map((r, i) => ({ r, s: toCreate[i] }))
        .filter(({ r }) => r.status === 'rejected')
        .map(({ r, s }) => {
          const reason = (r as PromiseRejectedResult).reason as { data?: { message?: string } } | undefined;
          const msg = reason?.data?.message || 'Something went wrong.';
          return `${s.label}: ${msg}`;
        });
      setCreateErrors(failures);
      return failures;
    } finally {
      setCreatingAll(false);
    }
  };

  // Single action button: save the note (if not yet saved) AND create every checked follow-up —
  // one click does both, labelled "Save" or "Save & create N" depending on what's checked.
  const handleSave = async () => {
    if (!saved) {
      await updateNote({
        id: note.note_id,
        body: { clean_transcript: transcript, custom_tags: tags, note_type: noteType, context: contextBody() },
      }).unwrap().catch(() => undefined);
      const savedNote = await saveNote(note.note_id).unwrap();
      onSaved(savedNote);
    }
    const failures = await createAllSelected();
    onNotesListChanged?.();
    // If any follow-up failed to create, stay on the card so the error banner (rendered near the
    // suggestions) is visible — closing would hide it and look like the whole thing silently failed.
    if (!failures.length) onDone();
  };

  // Expose the batch-create so the SAM EXECUTE_NOTE_FOLLOWUPS action can trigger it directly
  // (voice: "create everything" while the note is still open, saved or not).
  useEffect(() => {
    if (onExecuteFollowupsRef) onExecuteFollowupsRef.current = handleSave;
    return () => { if (onExecuteFollowupsRef) onExecuteFollowupsRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveNote, included, suggestionFields, saved, onExecuteFollowupsRef]);

  // Check/uncheck one, several, or ALL pending suggestions — the same state the checkbox in
  // SuggestionCards toggles, just driven by SAM's set_note_followup_included tool instead of a
  // click. Omitting both suggestionId/suggestionIds applies to every pending (not yet
  // created/dismissed) suggestion — "select all" / "unselect all".
  const toggleFollowups = (opts: { suggestionId?: string; suggestionIds?: string[]; included: boolean }) => {
    const ids = opts.suggestionIds?.length
      ? opts.suggestionIds
      : opts.suggestionId
      ? [opts.suggestionId]
      : (liveNote.suggestions || []).filter((s) => !s.confirmed && !s.dismissed).map((s) => s.suggestion_id);
    setIncluded((prev) => {
      const next = { ...prev };
      for (const id of ids) next[id] = opts.included;
      return next;
    });
  };

  useEffect(() => {
    if (onToggleFollowupsRef) onToggleFollowupsRef.current = toggleFollowups;
    return () => { if (onToggleFollowupsRef) onToggleFollowupsRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveNote.suggestions, onToggleFollowupsRef]);

  // Expand a follow-up's review panel (mirrors clicking "Review") and optionally prefill fields —
  // SAM's open_note_followup tool. Only one of the two SuggestionCards below is ever mounted
  // (pre-save vs. post-save), so both pass this SAME ref; whichever one is on screen registers
  // its own imperative expand function into it.
  const suggestionCardsExpandRef = useRef<((suggestionId: string, fields?: Record<string, unknown>) => void) | null>(null);
  useEffect(() => {
    if (!onExpandFollowupRef) return;
    onExpandFollowupRef.current = (opts) => suggestionCardsExpandRef.current?.(opts.suggestionId, opts.fields);
    return () => { onExpandFollowupRef.current = null; };
  }, [onExpandFollowupRef]);

  // ── Shared header ────────────────────────────────────────────────────────────
  const header = (
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-semibold text-ui-text">{t('noteTaker.reviewTitle')}</h3>
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[liveNote.status] || 'bg-ui-subtle text-ui-text3'}`}>
        {t(`noteTaker.status.${liveNote.status}`, { defaultValue: liveNote.status })}
      </span>
    </div>
  );

  // ── Post-save: meeting context → just Done ───────────────────────────────────
  if (saved && liveNote.context?.context_type === 'meeting') {
    return (
      <div className="card-workspace flex flex-col gap-3 p-4">
        {header}
        <div className="flex justify-end">
          <button type="button" onClick={onDone}
            className="rounded-lg btn-sheen px-4 py-2 text-sm font-semibold text-on-primary transition">
            {t('noteTaker.done', 'Done')}
          </button>
        </div>
      </div>
    );
  }

  // ── Post-save: follow-ups ────────────────────────────────────────────────────
  if (saved) {
    return (
      <div className="card-workspace flex flex-col gap-3 p-4">
        {header}

        {liveNote.analysis_stale && (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-warning/40 bg-warning/10 p-2">
            <p className="text-xs text-warning-text">{t('noteTaker.analysisStale', 'Analysis may be outdated.')}</p>
            <button type="button" disabled={regenerating} onClick={() => regenerate(liveNote.note_id)}
              className="shrink-0 rounded-md bg-warning-fill px-2 py-1 text-xs font-medium text-white disabled:opacity-50">
              {t('noteTaker.regenerate', 'Regenerate')}
            </button>
          </div>
        )}

        {followUpSummary && (
          <div className="rounded-lg bg-ui-subtle px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-ui-text3 mb-1">{t('noteTaker.summary')}</p>
            <p className="text-sm text-ui-text leading-snug">{followUpSummary}</p>
          </div>
        )}

        {liveNote.suggestions?.length > 0 ? (
          <SuggestionCards
            noteId={note.note_id}
            suggestions={liveNote.suggestions}
            included={included}
            onToggleIncluded={(id) => setIncluded((prev) => ({ ...prev, [id]: !(prev[id] ?? true) }))}
            onFieldsChange={(id, body) => setSuggestionFields((prev) => ({ ...prev, [id]: body }))}
            onOpenTask={onOpenTask}
            expandRef={suggestionCardsExpandRef}
          />
        ) : (
          <p className="text-sm text-ui-text3">{t('noteTaker.noSuggestions', 'No follow-ups suggested.')}</p>
        )}

        {createErrors.length > 0 && (
          <div className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2">
            <p className="text-xs font-semibold text-danger-text mb-1">
              {t('noteTaker.someFailed', { count: createErrors.length, defaultValue: '{{count}} follow-up(s) could not be created:' })}
            </p>
            {createErrors.map((e, i) => <p key={i} className="text-xs text-danger-text leading-snug">{e}</p>)}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            disabled={creatingAll}
            onClick={pendingSuggestionCount > 0 ? async () => { const failures = await createAllSelected(); if (!failures.length) onDone(); } : onDone}
            className="rounded-lg btn-sheen px-4 py-2 text-sm font-semibold text-on-primary transition disabled:opacity-50"
          >
            {creatingAll
              ? t('noteTaker.creating', 'Creating…')
              : pendingSuggestionCount > 0
              ? t('noteTaker.createSelectedCount', { count: pendingSuggestionCount, defaultValue: 'Create {{count}}' })
              : t('noteTaker.done', 'Done')}
          </button>
        </div>
      </div>
    );
  }

  // ── Pre-save: single flowing card ────────────────────────────────────────────
  return (
    <div className="card-workspace flex flex-col gap-0 overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        {header}
      </div>

      {/* ── Section 1: Transcript ── */}
      <div className="px-4 pb-3">
        <p className="mb-1.5 text-micro font-bold uppercase tracking-wide text-ui-text3">
          {t('noteTaker.noteText', 'Note · editable')}
        </p>
        <textarea
          className="w-full min-h-[80px] resize-none rounded-xl border border-ui-border bg-ui-input px-3 py-2 text-sm leading-relaxed text-ui-text focus:border-ui-ring/50 focus:outline-none"
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          rows={3}
        />
        {dirty && (
          <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-warning/40 bg-warning/10 px-2.5 py-2">
            <p className="text-xs text-warning-text leading-snug">
              {t('noteTaker.editedReanalyze', 'Edited — re-analyze to refresh follow-ups.')}
            </p>
            <button type="button" disabled={analyzing || !transcript.trim()} onClick={runReanalyze}
              className="shrink-0 rounded-md bg-warning-fill px-2 py-1 text-xs font-medium text-white disabled:opacity-50">
              {analyzing ? '…' : t('noteTaker.reanalyze')}
            </button>
          </div>
        )}
        {!analysed && !dirty && (
          <button type="button" disabled={busy || !transcript.trim()} onClick={runReanalyze}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg btn-sheen px-3 py-1.5 text-xs font-semibold text-on-primary disabled:opacity-50">
            <IconSparkle className="h-3.5 w-3.5" />
            {analyzing ? t('noteTaker.analysing', 'Analysing…') : t('noteTaker.analyse', 'Analyse')}
          </button>
        )}
      </div>

      <div className="mx-4 h-px bg-ui-border" />

      {/* ── Section 2: Classify ── */}
      <div className="px-4 py-3">
        <p className="mb-2 text-micro font-bold uppercase tracking-wide text-ui-text3">
          {t('noteTaker.classify', 'Classify')}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {allTags.map((tag) => {
            const on = tags.includes(tag);
            return (
              <button key={tag} type="button" onClick={() => toggleTag(tag)}
                className={`rounded-full border px-2.5 py-1 text-xs transition ${
                  on ? 'border-accent bg-accent/10 text-accent-text font-medium' : 'border-ui-border text-ui-text3 hover:bg-ui-hover'
                }`}>
                {t(`noteTaker.tagLabels.${tag}`, tag.replace(/_/g, ' '))}
              </button>
            );
          })}
        </div>

        {/* SAM classification message */}
        {analysed && (
          <div className="mt-2.5 flex items-start gap-2 rounded-xl border border-accent/30 bg-accent/5 px-3 py-2">
            <IconSparkle className="h-3.5 w-3.5 mt-0.5 text-accent-text shrink-0" />
            <p className="text-xs text-ui-text leading-snug">
              {t('noteTaker.classifyMessage', {
                count: tags.length,
                defaultValue: "I picked {{count}} categories. Tap to fix any.",
              })}
            </p>
          </div>
        )}
      </div>

      {/* ── Section 3: Follow-up suggestions (pre-save) ── */}
      {analysed && (liveNote.suggestions?.length ?? 0) > 0 && (
        <>
          <div className="mx-4 h-px bg-ui-border" />
          <div className="px-4 py-3">
            <p className="mb-2 text-micro font-bold uppercase tracking-wide text-ui-text3">
              {t('noteTaker.followUps', 'Follow-ups')}
            </p>
            <SuggestionCards
              noteId={note.note_id}
              suggestions={liveNote.suggestions}
              included={included}
              onToggleIncluded={(id) => setIncluded((prev) => ({ ...prev, [id]: !(prev[id] ?? true) }))}
              onFieldsChange={(id, body) => setSuggestionFields((prev) => ({ ...prev, [id]: body }))}
              onOpenTask={onOpenTask}
              expandRef={suggestionCardsExpandRef}
            />
          </div>
        </>
      )}

      <div className="mx-4 h-px bg-ui-border" />

      {/* ── Section 4: Link to ── */}
      <div className="px-4 py-3">
        <p className="mb-1.5 text-micro font-bold uppercase tracking-wide text-ui-text3">
          {t('noteTaker.linkContext')}
        </p>
        <div className="flex flex-col gap-2">
          <select
            className="w-full rounded-xl border border-ui-border bg-ui-input px-3 py-2 text-sm text-ui-text [color-scheme:light]"
            value={linkType}
            onChange={(e) => { setLinkType(e.target.value); setPropertyId(''); setMeetingId(''); }}
          >
            <option value="">{t('noteTaker.noContext')}</option>
            <option value="property">{t('noteTaker.contexts.property', 'Property')}</option>
            <option value="meeting">{t('noteTaker.contexts.meeting', 'Meeting')}</option>
          </select>
          {linkType === 'property' && (
            <select
              className="w-full rounded-xl border border-ui-border bg-ui-input px-3 py-2 text-sm text-ui-text [color-scheme:light]"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
            >
              <option value="">{t('noteTaker.selectProperty', 'Select a property…')}</option>
              {properties.map((p) => (
                <option key={p.property_id} value={p.property_id}>
                  {p.address?.normalized_address || [
                    p.address?.street_number, p.address?.street_name, p.address?.street_suffix,
                  ].filter(Boolean).join(' ')}
                </option>
              ))}
            </select>
          )}
          {linkType === 'meeting' && (
            <select
              className="w-full rounded-xl border border-ui-border bg-ui-input px-3 py-2 text-sm text-ui-text [color-scheme:light]"
              value={meetingId}
              onChange={(e) => setMeetingId(e.target.value)}
            >
              <option value="">{t('noteTaker.selectMeeting', 'Select a meeting…')}</option>
              {meetings.map((m) => (
                <option key={m.meeting_id} value={m.meeting_id}>{m.title}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {createErrors.length > 0 && (
        <div className="mx-4 mb-2 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2">
          <p className="text-xs font-semibold text-danger-text mb-1">
            {t('noteTaker.someFailed', { count: createErrors.length, defaultValue: '{{count}} follow-up(s) could not be created:' })}
          </p>
          {createErrors.map((e, i) => <p key={i} className="text-xs text-danger-text leading-snug">{e}</p>)}
        </div>
      )}

      {/* ── Action bar ── */}
      <div className="flex items-center justify-end gap-2 border-t border-ui-border bg-ui-subtle px-4 py-3">
        <button type="button" disabled={busy || creatingAll} onClick={handleDiscard}
          className="rounded-lg border border-ui-border px-3 py-2 text-sm text-ui-text disabled:opacity-50 hover:bg-ui-hover transition-colors">
          {t('noteTaker.discard')}
        </button>
        {/* Single action: saves the note and creates every checked follow-up in one click.
            Label reflects what's checked — "Save" with nothing checked, "Save & create N"
            when N follow-ups are selected. */}
        <button type="button" disabled={!canSave || creatingAll} onClick={handleSave}
          className="rounded-lg btn-sheen px-4 py-2 text-sm font-semibold text-on-primary transition disabled:opacity-50">
          {busy || creatingAll
            ? t('noteTaker.analysing', 'Saving…')
            : pendingSuggestionCount > 0
            ? t('noteTaker.saveAndCreateCount', { count: pendingSuggestionCount, defaultValue: 'Save & create {{count}}' })
            : t('noteTaker.saveMeetingNote', 'Save note')}
        </button>
      </div>
    </div>
  );
}

export default NoteReviewCard;
